import { z } from 'zod'
import { APP_ROLES } from '../auth-roles'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../password'
import { SIGNUP_COUNTRIES } from '../validation/age'

const passwordField = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`)

export const registerUserRequestSchema = z.object({
  email: z.string().email(),
  password: passwordField,
  displayName: z.string().trim().min(1).optional(),
  /** `YYYY-MM-DD`; required by current builds, optional on the wire for builds still in the field. */
  dateOfBirth: z.string().date().optional(),
  /** Where the person lives; sets the minimum age. Builds without it get the Austrian 14. */
  country: z.enum(SIGNUP_COUNTRIES).optional(),
  emailRedirectTo: z.string().url().optional(),
  /** The app's language; the confirmation email and later account emails follow it. Default German. */
  locale: z.enum(['de', 'en']).optional(),
})

/**
 * Admin-initiated business invite. Creates the business + invites the owner
 * by email; the invitee later completes via `completeInviteRequestSchema`.
 * No password here — the invitee sets it on completion.
 */
export const inviteBusinessRequestSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().email(),
  companyName: z.string().trim().min(1),
  legalName: z.string().trim().min(1),
  registrationNumber: z.string().nullable(),
  emailRedirectTo: z.string().url(),
})

/**
 * Self-service business registration: e-mail and password only. The names start
 * as placeholders from the e-mail; the owner sets up the profile and the company
 * details after the first sign-in.
 */
export const registerBusinessRequestSchema = z.object({
  email: z.string().email(),
  password: passwordField,
  emailRedirectTo: z.string().url(),
  /** From a partner link (`?partner=`); an unknown or malformed code is ignored. */
  partnerCode: z.string().max(100).optional(),
})

/**
 * Admin-initiated generic invite. Creates an auth user with the requested
 * roles + sends them an email invite; the invitee later completes via
 * `completeInviteRequestSchema`. `businessId` is required when any of the
 * roles is a business role and forbidden otherwise — enforced by the
 * service layer (see `validateInviteInput` in services/account-invite.ts).
 */
export const inviteAccountRequestSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().email(),
  roles: z.array(z.enum(APP_ROLES)).min(1),
  businessId: z.string().uuid().nullable(),
  emailRedirectTo: z.string().url(),
})

/**
 * PUBLIC password-set endpoint. The `accessToken` is the JWT Supabase
 * embeds in the invite / recovery email link's URL fragment — passing it
 * to the backend lets us verify the OTP-issued session cryptographically
 * via `supabaseAdmin.auth.getUser(accessToken)`. We do NOT accept a
 * normal Bearer session via `requireAuth` for this flow; the token IS
 * the proof. Pairs with the auth-required `/password/change` to give
 * exactly two password-setting paths to end users: the code-based one
 * (this) and the current-password-based one.
 */
export const setPasswordRequestSchema = z.object({
  accessToken: z.string().min(1),
  newPassword: passwordField,
})

/**
 * In-app password change for an already-authenticated, password-holding
 * user. `currentPassword` is required as proof-of-identity so a stolen
 * session can't silently rotate the password. Reset-flow completion goes
 * through a separate route that verifies the recovery token instead.
 */
export const updatePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: passwordField,
})

/**
 * Authenticated upgrade of an existing personal account to a business account.
 * Carries only the business fields — identity comes from the bearer token, so
 * there is deliberately no email/password here. Names left out start as
 * placeholders from the account's e-mail, like a self-service sign-up.
 */
export const upgradeToBusinessRequestSchema = z.object({
  firstName: z.string().trim().min(1).optional(),
  lastName: z.string().trim().min(1).optional(),
  companyName: z.string().trim().min(1).optional(),
  legalName: z.string().trim().min(1).optional(),
  registrationNumber: z.string().nullable().optional(),
  /** From a partner link (`?partner=`); an unknown or malformed code is ignored. */
  partnerCode: z.string().max(100).optional(),
})

export const upgradeToBusinessResponseSchema = z.object({
  businessId: z.string().uuid(),
})

/**
 * Self-register response. `upgraded` is `true` only when the email already
 * belonged to an account whose password was verified on submit and which was
 * upgraded to a business account in place — then `businessId` is set and the
 * client signs in and lands on the dashboard. Otherwise a fresh account was
 * created and `requiresEmailVerification` is `true`.
 */
/**
 * Which product asked. It only picks between our own hardcoded sign-in URLs, so
 * a caller can never steer the mail off-domain; omitted means the consumer app,
 * which keeps builds that predate the field working.
 */
export const accountSurfaceSchema = z.enum(['app', 'business', 'admin'])

export const resendConfirmationRequestSchema = z.object({
  email: z.string().email(),
  emailRedirectTo: z.string().url().optional(),
  surface: accountSurfaceSchema.optional(),
})

/**
 * Always `{ sent: true }`. A confirmed address gets the account-exists notice
 * and an unconfirmed one gets a fresh link, so the response cannot be used to
 * probe which emails exist or which are confirmed.
 */
export const resendConfirmationResponseSchema = z.object({
  sent: z.literal(true),
})

export const registerResponseSchema = z.object({
  requiresEmailVerification: z.boolean(),
  upgraded: z.boolean().default(false),
  businessId: z.string().uuid().nullable().default(null),
})

/**
 * Whether the password change also revoked the account's other sessions.
 * Revocation is best-effort server-side (the password is already rotated by
 * then), so a `false` here means the rotation succeeded but sessions taken
 * with the old password may still be alive — the clients report that.
 */
const sessionsRevokedShape = {
  sessionsRevoked: z.boolean().default(true),
}

/**
 * `/password/set` returns the user's email so the client can navigate to
 * the right login page with the field pre-filled — no client-side JWT
 * decode required.
 */
export const setPasswordResponseSchema = z.object({
  email: z.string().email(),
  ...sessionsRevokedShape,
})

/** `/password/change` reports only whether the other sessions were dropped. */
export const changePasswordResponseSchema = z.object(sessionsRevokedShape)

export const registerBusinessResponseSchema = z.object({
  businessId: z.string().uuid(),
  authUserId: z.string().uuid(),
  email: z.string().email(),
  /**
   * True when the invite attached a business to an account that already existed
   * (owner keeps their password, gets a "business ready" mail) rather than
   * creating a fresh account with a set-password invite. Admin-only response.
   */
  existingAccount: z.boolean().default(false),
  /**
   * Whether the owner's mail (set-password invite for a fresh account, or the
   * "business ready" notice for an existing one) was sent. False means the
   * business was still created but the admin must notify the owner manually.
   */
  notificationSent: z.boolean().default(true),
})

export const inviteAccountResponseSchema = z.object({
  authUserId: z.string().uuid(),
  email: z.string().email(),
})

export type UpgradeToBusinessRequest = z.infer<typeof upgradeToBusinessRequestSchema>
export type UpgradeToBusinessResponse = z.infer<typeof upgradeToBusinessResponseSchema>
export type RegisterUserRequest = z.infer<typeof registerUserRequestSchema>
export type RegisterBusinessRequest = z.infer<typeof registerBusinessRequestSchema>
export type InviteBusinessRequest = z.infer<typeof inviteBusinessRequestSchema>
export type InviteAccountRequest = z.infer<typeof inviteAccountRequestSchema>
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>
export type UpdatePasswordRequest = z.infer<typeof updatePasswordRequestSchema>
export type RegisterResponse = z.infer<typeof registerResponseSchema>
export type AccountSurface = z.infer<typeof accountSurfaceSchema>
export type ResendConfirmationRequest = z.infer<typeof resendConfirmationRequestSchema>
export type ResendConfirmationResponse = z.infer<typeof resendConfirmationResponseSchema>
export type SetPasswordResponse = z.infer<typeof setPasswordResponseSchema>
export type ChangePasswordResponse = z.infer<typeof changePasswordResponseSchema>
export type RegisterBusinessResponse = z.infer<typeof registerBusinessResponseSchema>
export type InviteAccountResponse = z.infer<typeof inviteAccountResponseSchema>
